import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import posthog from "posthog-js";
import { client } from "../client";
import {
  AuthError,
  ChangePasswordCredentials,
  ChangePasswordResponse,
  CURRENT_USER_QUERY_KEY,
  ForgotPasswordCredentials,
  ForgotPasswordResponse,
  LinkStravaCredentials,
  LinkStravaResponse,
  LoginCredentials,
  LoginResponse,
  RegisterCredentials,
  RegisterResponse,
  ResetPasswordCredentials,
  ResetPasswordResponse,
  SendVerificationEmailResponse,
  UnlinkStravaResponse,
  VerifyEmailCredentials,
  VerifyEmailResponse,
} from "./types";

/**
 * Hook to fetch and access the current user data
 */
export function useCurrentUser() {
  const navigate = useNavigate();

  return useQuery({
    queryKey: CURRENT_USER_QUERY_KEY,
    queryFn: async () => {
      const response = await client.auth.me.$get();

      if (!response.ok) {
        // If unauthorized or other error, clear user from store
        if (response.status === 401) {
          localStorage.removeItem("auth_token");
          navigate({ to: "/login", reloadDocument: true });
        }

        throw new Error("Failed to fetch user data");
      }

      const data = await response.json();

      posthog.identify(data.id, {
        email: data.email,
        username: data.firstName + " " + data.lastName,
      });

      return data;
    },
    // Don't run the query if there's no token
    enabled: !!localStorage.getItem("auth_token"),
  });
}

/**
 * Hook for user login functionality
 * Returns a mutation that handles login and stores the token and user data
 */
export function useLogin() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation<LoginResponse, AuthError, LoginCredentials>({
    mutationFn: async (credentials) => {
      const response = await client.auth.login.$post({
        json: credentials,
      });

      if (!response.ok) {
        const errorData = (await response.json()) as { message?: string };
        throw new Error(errorData.message || "Login failed");
      }

      return await response.json();
    },
    onSuccess: (data) => {
      // Store the token in localStorage
      localStorage.setItem("auth_token", data.token);

      // Invalidate and refetch the current user query
      queryClient.invalidateQueries({ queryKey: CURRENT_USER_QUERY_KEY });

      navigate({ to: "/home", reloadDocument: true });
    },
  });
}

/**
 * Hook for user registration functionality
 * Returns a mutation that handles registration and stores the token
 */
export function useRegister() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation<RegisterResponse, AuthError, RegisterCredentials>({
    mutationFn: async (credentials) => {
      const response = await client.auth.register.$post({
        json: credentials,
      });

      if (!response.ok) {
        const errorData = (await response.json()) as { message?: string };
        throw new Error(errorData.message || "Registration failed");
      }

      return await response.json();
    },
    onSuccess: (data) => {
      // Store the token in localStorage
      localStorage.setItem("auth_token", data.token);

      // Invalidate and refetch the current user query
      queryClient.invalidateQueries({ queryKey: CURRENT_USER_QUERY_KEY });

      // Navigate to home after successful registration
      navigate({ to: "/home", reloadDocument: true });
    },
  });
}

/**
 * Hook for email verification functionality
 * Returns a mutation that handles email verification
 */
export function useVerifyEmail() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation<VerifyEmailResponse, AuthError, VerifyEmailCredentials>({
    mutationFn: async (credentials) => {
      const response = await client.auth["verify-email"].$post({
        json: credentials,
      });

      if (!response.ok) {
        const errorData = (await response.json()) as { message?: string };
        throw new Error(errorData.message || "Email verification failed");
      }

      return await response.json();
    },
    onSuccess: () => {
      // Invalidate and refetch the current user query to update verification status
      queryClient.invalidateQueries({ queryKey: CURRENT_USER_QUERY_KEY });

      // Navigate to home after successful verification
      navigate({ to: "/home", reloadDocument: true });
    },
  });
}

/**
 * Hook for sending verification email
 * Returns a mutation that handles sending a verification email to the user
 */
export function useSendVerificationEmail() {
  const queryClient = useQueryClient();

  return useMutation<SendVerificationEmailResponse, AuthError, void>({
    mutationFn: async () => {
      const response = await client.auth["send-verification-email"].$post();

      if (!response.ok) {
        const errorData = (await response.json()) as { message?: string };
        throw new Error(
          errorData.message || "Failed to send verification email"
        );
      }

      return await response.json();
    },
    onSuccess: () => {
      // Invalidate and refetch the current user query
      queryClient.invalidateQueries({ queryKey: CURRENT_USER_QUERY_KEY });
    },
  });
}

/**
 * Hook for changing user password
 * Returns a mutation that handles password change
 */
export function useChangePassword() {
  return useMutation<
    ChangePasswordResponse,
    AuthError,
    ChangePasswordCredentials
  >({
    mutationFn: async (credentials) => {
      const response = await client.auth["change-password"].$patch({
        json: credentials,
      });

      if (!response.ok) {
        // Handle different error cases
        if (response.status === 403) {
          throw new Error("Current password is incorrect");
        }

        const errorData = (await response.json()) as { message?: string };
        throw new Error(errorData.message || "Failed to change password");
      }

      return await response.json();
    },
  });
}

/**
 * Hook for initiating the password reset process
 * Returns a mutation that sends a password reset email to the user
 */
export function useForgotPassword() {
  return useMutation<
    ForgotPasswordResponse,
    AuthError,
    ForgotPasswordCredentials
  >({
    mutationFn: async (credentials) => {
      const response = await client.auth["forgot-password"].$post({
        json: credentials,
      });

      if (!response.ok) {
        const errorData = (await response.json()) as { message?: string };
        throw new Error(
          errorData.message || "Failed to send password reset email"
        );
      }

      return await response.json();
    },
  });
}

/**
 * Hook for resetting user password using token from email
 * Returns a mutation that handles password reset
 */
export function useResetPassword() {
  return useMutation<
    ResetPasswordResponse,
    AuthError,
    ResetPasswordCredentials
  >({
    mutationFn: async (credentials) => {
      const response = await client.auth["reset-password"].$post({
        json: credentials,
      });

      if (!response.ok) {
        // Handle different error cases
        if (response.status === 403) {
          throw new Error("Invalid or expired token");
        }

        const errorData = (await response.json()) as { message?: string };
        throw new Error(errorData.message || "Failed to reset password");
      }

      return await response.json();
    },
  });
}

/**
 * Hook for linking Strava account
 * Returns a mutation that handles sending the authorization code to the server
 */
export function useLinkStrava() {
  const queryClient = useQueryClient();

  return useMutation<LinkStravaResponse, AuthError, LinkStravaCredentials>({
    mutationFn: async (credentials) => {
      const response = await client.strava.link.$post({
        json: credentials,
      });

      if (!response.ok) {
        const errorData = (await response.json()) as { message?: string };
        throw new Error(errorData.message || "Failed to link Strava account");
      }

      return await response.json();
    },
    onSuccess: () => {
      // Invalidate and refetch the current user query to update Strava connection status
      queryClient.invalidateQueries({ queryKey: CURRENT_USER_QUERY_KEY });
    },
  });
}

/**
 * Hook for unlinking Strava account
 * Returns a mutation that handles disconnecting the user's Strava account
 */
export function useUnlinkStrava() {
  const queryClient = useQueryClient();

  return useMutation<UnlinkStravaResponse, AuthError, void>({
    mutationFn: async () => {
      const response = await client.strava.unlink.$post();

      if (!response.ok) {
        const errorData = (await response.json()) as { message?: string };
        throw new Error(errorData.message || "Failed to unlink Strava account");
      }

      return await response.json();
    },
    onSuccess: () => {
      // Invalidate and refetch the current user query to update Strava connection status
      queryClient.invalidateQueries({ queryKey: CURRENT_USER_QUERY_KEY });
    },
  });
}
